import { useForm } from "react-hook-form";

import Typography from "@/design-system/Typography";
import Button from "@/design-system/button";
import ModalSkeleton from "@/design-system/modal-skeleton";
import TextInput from "@/design-system/text-input";
import { Post } from "@/lib/fetch-helper";
import { emailRegex } from "@/lib/regex";
import { languageSelector } from "@/src/contexts";
import { useMutationCustom } from "@/src/query-client-overrides";
import { Loading } from "@Components/Loading";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, colors } from "../../../styles/tokens.stylex";
import { ForgetPasswordFormResources } from "./ForgetPasswordForm.Resources";
import { ForgetPasswordFormResourcesEn } from "./ForgetPasswordForm.Resources.En";

export interface IForgetPasswordForm {
    onBackButton: () => void;
}

type ForgetPasswordResponse = boolean | { email: "required" | "invalid" };
type ForgetPasswordRequest = { email: string };

export default function ForgetPassword({ onBackButton }: IForgetPasswordForm) {
    const resources = languageSelector(ForgetPasswordFormResources, ForgetPasswordFormResourcesEn);
    const t = (key: keyof typeof resources) => resources[key];
    const { register, getValues, handleSubmit, formState, setError } = useForm<ForgetPasswordRequest>();

    const { data, mutate, isPending } = useMutationCustom({
        mutationFn: (values: ForgetPasswordRequest) =>
            Post<ForgetPasswordResponse, ForgetPasswordRequest>("/account/ForgotPassword", { arg: values }),
        onSuccess: (response) => {
            if (typeof response === "object") {
                setError("email", {
                    message: response.email === "required" ? t("emailRequiredError") : t("emailInvalidError"),
                });
            }
        },
    });

    const renderContent = () => {
        let content: React.ReactNode | null = null;

        if (data === true) {
            content = (
                <>
                    <Typography>
                        {t("sentMessageOne")} {getValues("email")}
                    </Typography>
                    <Typography>{t("sentMessageTwo")}</Typography>
                </>
            );
        } else {
            content = (
                <>
                    <div>{t("message")}</div>
                    <div>
                        <TextInput
                            variant="md"
                            type="email"
                            placeholder={t("emailWatermark")}
                            {...register("email", {
                                required: t("emailRequiredError"),
                                pattern: {
                                    value: emailRegex,
                                    message: t("emailInvalidError"),
                                },
                            })}
                            errorMessage={formState?.errors?.email?.message}
                        />
                    </div>
                    {isPending ? (
                        <Loading />
                    ) : (
                        <Button
                            as="button"
                            variant="magentaRound"
                            type="submit"
                            size="M"
                            disabled={isPending}
                        >
                            {t("recoveryButton")}
                        </Button>
                    )}
                </>
            );
        }

        return content;
    };

    return (
        <ModalSkeleton wrapperStyleX={styles.modal}>
            <Typography styleX={styles.modalTitle}>{t("modalHeaderText")}</Typography>
            <form onSubmit={handleSubmit((values) => mutate(values))} {...stylex.props(styles.formContainer)}>
                {renderContent()}
                <Button as="button" variant="secondary" type="button" size="M" onClick={onBackButton}>
                    {t("backToLogin")}
                </Button>
            </form>
        </ModalSkeleton>
    );
}

const styles = stylex.create({
    modal: {
        padding: ComposableSpacing.s6,
        width: "360px",
    },
    modalTitle: {
        padding: 0,
        color: colors.b_BrandNavy,
        fontSize: "1.125rem",
        fontWeight: "normal",

        textAlign: "center",
        textTransform: "uppercase",
        marginBottom: 0,
        marginLeft: "auto",
        marginRight: "auto",
        marginTop: 0,
        paddingBottom: ComposableSpacing.s6,
    },
    formContainer: {
        gap: "1.5rem",
        display: "flex",
        flexDirection: "column",
    },
});
